import { useEffect } from 'react';

/**
 * Start downloading mapbox-gl in the background on pages that have maps.
 *
 * react-map-gl loads mapbox-gl lazily, the moment the first <Map> mounts. That keeps it out of
 * app startup, but it means the first map opened in a session waits on a ~500 kB (gzipped)
 * download before it can draw anything (Michael Wallace, 2026-10-14: maps are sluggish). Fetching
 * it once the page is idle means the library is usually already there when the user opens a map.
 *
 * `import('mapbox-gl')` resolves to the same chunk react-map-gl loads, so nothing downloads twice.
 */
let started = false;

function prefetch(): void {
  if (started) return;
  started = true;
  const run = () => {
    import('mapbox-gl').catch(() => {
      // A failed prefetch just means the map loads it on open, as before.
      started = false;
    });
  };
  const idle = (window as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void })
    .requestIdleCallback;
  if (idle) idle(run, { timeout: 3000 });
  else setTimeout(run, 1500);
}

export function usePrefetchMapLibrary(): void {
  useEffect(() => {
    prefetch();
  }, []);
}
